import { Autocomplete } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';
import PmiTextField from 'shell/components/shared/text-field';
import debounce from 'shell/utils/debounce';
import { noop } from 'shell/utils/noop';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { CirculationArea } from 'shell/models/circulation-area';
import { StreetCohabitationItemVM } from 'shell/view-models/street-cohabitation';

interface StreetAutoCompleteProps {
  disabled?: boolean;
  value?: StreetCohabitationItemVM | null;
  onChange: (item: StreetCohabitationItemVM | null) => void;
  error?: boolean;
  required?: boolean;
  label?: string;
  id?: string;
}

const StreetCohabitationAutoCompleteComponent = ({
  disabled,
  onChange,
  required,
  value: selectedValue,
  error,
  label = 'Via convivenza',
  id = 'cohabitationStreet',
}: StreetAutoCompleteProps): React.ReactElement => {
  const { userProfile } = useUserProfile();
  const [streets, getStreets] = useHttpFetch<
    HttpResponseList<CirculationArea>,
    Array<StreetCohabitationItemVM>
  >(`${__TOPONYMY_API_URL__}/areaCircolazione/ricerca/tipoarea-like-descrizioneridotta`, {
    onBootstrap: false,
    initialData: [],
    options: {
      loading: false,
      errorCatching: true,
      method: 'POST',
    },
    transformer: (response: AxiosResponse<HttpResponseList<CirculationArea>>) => {
      const { data } = response;

      return data?.payload.list.map((item) => ({
        id: item.key?.codice,
        value: item.descrizioneRidotta,
        kindStreet: item.tipoArea,
        cohabitationId: item.idDbLocaleConvivenza,
      })) as Array<StreetCohabitationItemVM>;
    },
  });

  const defaultProps = useMemo(
    () => ({
      options: (streets.data as Array<StreetCohabitationItemVM>) || [],
      getOptionLabel: (option: StreetCohabitationItemVM) => `${option.value}`,
    }),
    [streets.data]
  );

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        const { abort = noop } = streets;

        abort();

        getStreets({
          data: {
            key: {
              ente: userProfile.datiLogin?.key?.codiceEnte,
            },
            descrizioneRidotta: value,
          },
        });
      },
      [streets.abort, getStreets, userProfile.datiLogin?.key?.codiceEnte]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      disabled={disabled}
      value={selectedValue}
      onChange={(event, element) => {
        event.stopPropagation();
        onChange(element);
      }}
      onInputChange={(event, value, reason) => {
        if (value.length >= 3 && reason === 'input') {
          handleSearch(value);
        }
      }}
      clearOnBlur={false}
      loading={streets.isLoading}
      style={{ flex: 1 }}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );
};

StreetCohabitationAutoCompleteComponent.displayName = 'Street cohabitation auto complete component';

const StreetCohabitationAutoComplete = memo(StreetCohabitationAutoCompleteComponent);

export default StreetCohabitationAutoComplete as typeof StreetCohabitationAutoCompleteComponent;
